'use client'

import { useState, FormEvent, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { register } from '@/app/actions/auth'
import GoogleAuthButton from '@/components/ui/google-auth-button'

export default function RegisterPage() {
  const router = useRouter()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [ci, setCI] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      const err = params.get('error')
      if (err) {
        if (err === 'GoogleAuthFailed') setError('La autenticación con Google falló.')
        else if (err === 'GoogleUserError') setError('No se pudo obtener la información de Google.')
        else setError('Error al autenticar con Google. Intenta nuevamente.')
      }
    }
  }, [])

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError('')

    // Validar que las contraseñas coincidan
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden')
      return
    }

    // Validar longitud de contraseña
    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres')
      return
    }

    setLoading(true)

    try {
      const result = await register({
        name: name.trim() || undefined,
        email: email.trim(),
        password,
        ci: ci.trim(),
      })

      if (result.success && result.data) {
        router.push('/dashboard')
        router.refresh()
      } else {
        setError(result.error || 'Error al registrar el usuario')
      }
    } catch (_err) {
      console.error('Registration error:', _err)
      setError('Error al registrar el usuario. Por favor, intenta nuevamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 lg:p-0 overflow-hidden bg-white">
      {/* Background Image full screen */}
      <div 
        className="absolute inset-0 z-0" 
        style={{ 
          backgroundImage: "url('/login-register/bg-login-register.webp')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-white/30 backdrop-blur-sm"></div>
      </div>

      <div className="max-w-6xl w-full px-4 md:px-8 py-8 relative z-10 flex flex-col md:flex-row items-center justify-between gap-10 md:gap-16 lg:gap-24">
        
        {/* Left Side: Floating Branding */}
        <div className="w-full md:w-1/2 flex flex-col items-center md:items-start text-center md:text-left drop-shadow-md">
          <div className="mb-4">
            <Link href="/" className="inline-block hover:opacity-90 transition-opacity">
              <span className="text-6xl md:text-[5rem] lg:text-[7rem] font-black tracking-tighter text-[#1e2c6e] flex items-center gap-2 md:gap-4 leading-none">
                <div className="flex flex-col gap-1.5 md:gap-1.5 items-center justify-center">
                  <div className="w-9 h-2 md:w-15 md:h-3.5 bg-[#1e2c6e] rounded-sm" />
                  <div className="w-12 h-2 md:w-20 md:h-3.5 bg-[#3341ab] rounded-sm transform -translate-x-1" />
                  <div className="w-9 h-2 md:w-15 md:h-3.5 bg-[#1e2c6e] rounded-sm transform translate-x-1" />
                </div>
                KERN
              </span>
            </Link>
          </div>
          <h2 className="text-xl md:text-2xl lg:text-3xl text-[#1e2c6e] font-medium leading-tight max-w-md drop-shadow-sm mix-blend-multiply opacity-90">
            Todo lo que necesita tu negocio, en un solo lugar.
          </h2>
        </div>

        {/* Right Side: Isolated Form Card */}
        <div className="w-full max-w-120 bg-white/95 backdrop-blur-md p-8 md:p-10 rounded-2xl shadow-xl border border-white/60">
          
          {error && (
            <div className="mb-5 p-3 bg-red-50/90 backdrop-blur-sm border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm font-medium text-center">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 border border-[#1e2c6e] rounded-lg focus:ring-2 focus:ring-[#3341ab]/20 focus:border-[#3341ab] outline-none transition bg-white text-slate-800 placeholder-slate-400 font-medium text-sm shadow-sm"
                placeholder="Nombre (opcional)"
              />
            </div>

            <div>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border border-[#1e2c6e] rounded-lg focus:ring-2 focus:ring-[#3341ab]/20 focus:border-[#3341ab] outline-none transition bg-white text-slate-800 placeholder-slate-400 font-medium text-sm shadow-sm"
                placeholder="Correo electrónico (será tu usuario)"
              />
            </div>

            <div>
              <input
                id="ci"
                type="text"
                value={ci}
                onChange={(e) => setCI(e.target.value)}
                required
                minLength={6}
                className="w-full px-4 py-3 border border-[#1e2c6e] rounded-lg focus:ring-2 focus:ring-[#3341ab]/20 focus:border-[#3341ab] outline-none transition bg-white text-slate-800 placeholder-slate-400 font-medium text-sm shadow-sm"
                placeholder="Carnet de Identidad (Requerido)"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="w-full">
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full px-4 py-3 border border-[#1e2c6e] rounded-lg focus:ring-2 focus:ring-[#3341ab]/20 focus:border-[#3341ab] outline-none transition bg-white text-slate-800 placeholder-slate-400 font-medium text-sm shadow-sm"
                  placeholder="Contraseña"
                />
              </div>

              <div className="w-full">
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full px-4 py-3 border border-[#1e2c6e] rounded-lg focus:ring-2 focus:ring-[#3341ab]/20 focus:border-[#3341ab] outline-none transition bg-white text-slate-800 placeholder-slate-400 font-medium text-sm shadow-sm"
                  placeholder="Confirmar contraseña"
                />
              </div>
            </div>

            {/* Terms checkbox */}
            <div className="flex items-center justify-center pt-2 pb-2">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input type="checkbox" required className="w-4 h-4 rounded border-[#1e2c6e] text-[#3341ab] focus:ring-[#3341ab]/20" />
                <span className="text-xs text-slate-700 font-medium group-hover:text-[#1e2c6e] transition-colors">
                  Acepto los <Link href="#" className="font-bold underline text-[#1e2c6e] hover:text-[#3341ab]">Términos de Servicios</Link> y <Link href="#" className="font-bold underline text-[#1e2c6e] hover:text-[#3341ab]">Política de Privacidad</Link>
                </span>
              </label>
            </div>

            <div className="pt-2 flex justify-center">
              <button
                type="submit"
                disabled={loading}
                className="w-full max-w-60 bg-[#1e2c6e] text-white h-12 rounded-full font-bold text-sm tracking-wide hover:bg-[#3341ab] transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5 cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? 'Creando cuenta...' : 'Crear cuenta'}
              </button>
            </div>
          </form>

          <div className="mt-10">
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-[10px]">
                <span className="px-3 bg-white text-slate-400 font-bold uppercase tracking-wider">o registrarse con</span>
              </div>
            </div>

            <div className="flex justify-center">
              <GoogleAuthButton />
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-slate-700 font-medium">
              ¿Ya está registrado?{' '}
              <Link href="/login" className="font-bold text-[#1e2c6e] hover:underline hover:text-[#3341ab] transition-colors">
                Iniciar sesión
              </Link>
            </p>
          </div>



          <div className="mt-6 text-center">
            <Link href="/" className="text-slate-400 hover:text-[#1e2c6e] text-[10px] font-bold uppercase tracking-[0.2em] transition-colors">
              ← Volver al inicio
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
